import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { categories } from "../../data/products";
import { api } from "../../lib/api";
import { Search } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  // Backend-served endpoints: /api/files/... (object storage) and legacy /uploads/*
  if (u.startsWith("/api/files/") || u.startsWith("/uploads/")) return `${BACKEND}${u}`;
  // Frontend static (e.g., /products/tiles/*.jpg)
  return u;
};

export const Catalog = () => {
  const [products, setProducts] = useState([]);
  const productCount = products.length;
  const [active, setActive] = useState("Toate");
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(24);

  useEffect(() => {
    api
      .get("/products")
      .then(({ data }) => setProducts(data))
      .catch(() => setProducts([]));
  }, []);

  const filtered = useMemo(() => {
    let list = products;
    if (active !== "Toate") list = list.filter((p) => p.category === active);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q)
      );
    }
    return list;
  }, [active, query, products]);

  const visible = filtered.slice(0, limit);

  return (
    <section
      id="catalog"
      data-testid="catalog-section"
      className="relative py-20 sm:py-24 bg-[#0A0908] border-y border-[#1A1411]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div>
            <p className="text-xs font-semibold tracking-[0.32em] uppercase text-[#C8A97E] mb-5">
              — Catalog Produse
            </p>
            <h2 className="font-[Playfair_Display] text-[#FDF8F5] text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.08] tracking-tight">
              Gama noastră <em className="italic text-[#C8A97E]">completă</em>
            </h2>
            <p className="mt-5 text-[#A8A29E] text-base font-light max-w-xl">
              Peste {productCount} de produse atent selecționate — de la
              marinate și conserve, la dulciuri, vinuri și cadouri festive.
            </p>
          </div>

          <div className="relative w-full lg:max-w-sm">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A8A29E]" size={18} strokeWidth={1.4} />
            <input
              data-testid="catalog-search"
              type="text"
              placeholder="Caută produs..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setLimit(24);
              }}
              className="w-full bg-[#0F0D0B] border border-[#2A2522] text-[#FDF8F5] placeholder:text-[#6B5E55] pl-12 pr-4 py-3.5 text-sm focus:border-[#C8A97E] focus:outline-none focus:ring-1 focus:ring-[#C8A97E] transition-colors"
            />
          </div>
        </div>

        {/* Category filters */}
        <div
          data-testid="category-filter"
          className="flex flex-wrap gap-2 mb-12 pb-8 border-b border-[#1A1411]"
        >
          {categories.map((cat) => {
            const isActive = active === cat;
            return (
              <button
                key={cat}
                data-testid={`filter-${cat}`}
                onClick={() => {
                  setActive(cat);
                  setLimit(24);
                }}
                className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-all duration-300 ${
                  isActive
                    ? "bg-[#C8A97E] text-[#080706] border-[#C8A97E]"
                    : "bg-transparent text-[#B8AC9F] border-[#2A2522] hover:border-[#C8A97E] hover:text-[#FDF8F5]"
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {/* Grid */}
        {visible.length === 0 ? (
          <div className="py-20 text-center text-[#A8A29E]" data-testid="catalog-empty">
            Niciun produs nu corespunde căutării.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
            {visible.map((p) => (
              <Link
                key={p.id}
                to={`/produs/${p.id}`}
                data-testid={`product-card-${p.id}`}
                className="group relative flex flex-col bg-[#FAF6EE] border border-[#E8DFCF] hover:border-[#C8A97E] hover:shadow-[0_30px_60px_-20px_rgba(200,169,126,0.35)] hover:-translate-y-1 transition-all duration-500 overflow-hidden"
              >
                <div className="relative aspect-[5/3] overflow-hidden bg-white flex items-center justify-center">
                  <img
                    src={absUrl(p.image)}
                    alt={p.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                  />
                  <span className="absolute top-3 left-3 z-10 text-[10px] uppercase tracking-[0.22em] text-[#7A5A2E] bg-white/95 backdrop-blur px-2.5 py-1 border border-[#C8A97E]/50">
                    {p.category}
                  </span>
                </div>
                <div className="p-6 flex flex-col flex-1 bg-[#FAF6EE]">
                  <h3 className="font-[Playfair_Display] text-[#1A1311] text-lg leading-snug min-h-[3rem]">
                    {p.name}
                  </h3>
                  {p.description && (
                    <p className="text-xs text-[#7A6A5A] mt-2 font-light line-clamp-2">
                      {p.description}
                    </p>
                  )}
                  {p.price != null && p.price !== "" && (
                    <p className="mt-3 font-[Playfair_Display] text-2xl text-[#1A1311]">
                      {Number(p.price).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{" "}
                      <span className="text-sm text-[#9B8466]">lei</span>
                    </p>
                  )}
                  <div className="mt-4 pt-4 border-t border-[#E8DFCF] flex items-center justify-between text-xs">
                    <div>
                      <p className="text-[#9B8466] uppercase tracking-[0.2em] text-[10px]">Gramaj</p>
                      <p className="text-[#1A1311] font-normal mt-1">{p.size || "—"}</p>
                    </div>
                    {p.pack && (
                      <div className="text-right">
                        <p className="text-[#9B8466] uppercase tracking-[0.2em] text-[10px]">Ambalaj</p>
                        <p className="text-[#1A1311] font-normal mt-1">{p.pack}</p>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {visible.length < filtered.length && (
          <div className="mt-14 flex justify-center">
            <button
              data-testid="load-more-btn"
              onClick={() => setLimit((l) => l + 24)}
              className="border border-[#3D3530] text-[#FDF8F5] bg-transparent px-8 py-4 text-sm font-medium tracking-wide hover:bg-[#0F0D0B] hover:border-[#C8A97E] transition-all duration-300"
            >
              Arată mai multe produse ({filtered.length - visible.length} rămase)
            </button>
          </div>
        )}

        <p
          data-testid="catalog-count"
          className="mt-10 text-center text-xs uppercase tracking-[0.22em] text-[#9D8B7B]"
        >
          {filtered.length} produse afișate · {productCount} produse în total
        </p>
      </div>
    </section>
  );
};
